import React, { useState } from "react";
import { RiArrowUpDownLine, RiArrowUpLine, RiArrowDownLine } from "@remixicon/react";
import { DashboardSortBy } from "../../../__generated__/api";
import Dropdown from "../../theme/dropdown/Dropdown";
import Styles from "./BoardToolbar.module.scss";
import Button from "../../theme/button/Button";

export enum Direction {
    ASCENDING,
    DESCENDING
}

export enum SortType {
    CREATED_DATE = "CREATE_DATE",
    LAST_RESPONSE_DATE = "LAST_RESPONSE_DATE",
    PRIORITY = "PRIORITY",
    TICKET_NUMBER = "TICKET_NUMBER"
}

export const SORT_BY_TO_NAME = [
    {
        sortBy: DashboardSortBy.CreatedDateAscending,
        type: SortType.CREATED_DATE,
        label: "Created Date",
        direction: Direction.ASCENDING,
    },
    {
        sortBy: DashboardSortBy.CreatedDateDescending,
        type: SortType.CREATED_DATE,
        label: "Created Date",
        direction: Direction.DESCENDING,
    },
    {
        sortBy: DashboardSortBy.LastResponseDateAscending,
        type: SortType.LAST_RESPONSE_DATE,
        label: "Last Response Date",
        direction: Direction.ASCENDING,
    },
    {
        sortBy: DashboardSortBy.LastResponseDateDescending,
        type: SortType.LAST_RESPONSE_DATE,
        label: "Last Response Date",
        direction: Direction.DESCENDING,
    },
    {
        sortBy: DashboardSortBy.PriorityAscending,
        type: SortType.PRIORITY,
        label: "Priority",
        direction: Direction.ASCENDING,
    },
    {
        sortBy: DashboardSortBy.PriorityDescending,
        type: SortType.PRIORITY,
        label: "Priority",
        direction: Direction.DESCENDING,
    },
    {
        sortBy: DashboardSortBy.TicketNumberAscending,
        type: SortType.TICKET_NUMBER,
        label: "Ticket Number",
        direction: Direction.ASCENDING,
    },
    {
        sortBy: DashboardSortBy.TicketNumberDescending,
        type: SortType.TICKET_NUMBER,
        label: "Ticket Number",
        direction: Direction.DESCENDING,
    },
];

export const SORT_BY_GROUPS = SORT_BY_TO_NAME.groupBy("type");

type SortByRow = (typeof SORT_BY_TO_NAME)[number];

function SortUpDown({
    onChange,
    value,
    options,
}: {
    onChange: (sortBy?: DashboardSortBy) => void;
    value: DashboardSortBy | undefined;
    options: SortByRow[];
}) {
    const selected = options.find(row => row.sortBy === value);
    const createOnClick = (direction: Direction) => (event: React.MouseEvent) => {
        event.stopPropagation();
        const clicked = options.find(row => row.direction === direction)?.sortBy;
        if (clicked) {
            onChange(clicked);
        }
    };
    return (
        <div>
            <Button
                size="small"
                className={Styles["btn-sort-order"]}
                variant={selected?.direction === Direction.ASCENDING ? "primary" : "ghost-gray"}
                isActive={selected?.direction === Direction.ASCENDING}
                prefixIcon={<RiArrowUpLine />}
                onClick={createOnClick(Direction.ASCENDING)}
                title="Ascending"
            />
            <Button
                size="small"
                className={Styles["btn-sort-order"]}
                variant={selected?.direction === Direction.DESCENDING ? "primary" : "ghost-gray"}
                onClick={createOnClick(Direction.DESCENDING)}
                title="Descending"
                prefixIcon={<RiArrowDownLine />}
            />
        </div>
    );
}

export function SortItem({
    type, value, onChange,
}: {
    type: SortType;
    value: DashboardSortBy | undefined;
    onChange: (sortBy?: DashboardSortBy) => void
}): React.JSX.Element {
    const group = SORT_BY_GROUPS[type];
    const groupLabel = group.firstElement.label;

    return (
        <Dropdown.Item
            className={Styles["panel-sort"]}
            onClick={() => {
                const selected = group.firstElement.sortBy === value ? group[1] : group.firstElement;
                onChange(selected.sortBy);
            }}
            postFixItem={<SortUpDown onChange={onChange} value={value} options={group} />}
        >
            {groupLabel}
        </Dropdown.Item>
    );
}

export default function SortByFilter({
    onChange,
    value,
    shouldShowExpand,
}: {
    onChange: (sortBy?: DashboardSortBy) => void;
    value: DashboardSortBy | undefined;
    shouldShowExpand?: boolean;
}) {
    const [shouldShow, setShouldShow] = useState(false);

    return (
        <Dropdown autoClose="outside" show={shouldShow} onToggle={() => setShouldShow(!shouldShow)}>
            <Dropdown.Toggle
                variant="filter"
                prefixIcon={<RiArrowUpDownLine />}
                onClick={() => {
                    setShouldShow(!shouldShow);
                }}
                className="d-flex align-items-center w-100 text-start"
                postfixIcon={shouldShowExpand ? <RiArrowDownLine /> : undefined}
                isActive={value && value !== DashboardSortBy.Manual}
            >
                <div className="flex-grow-1">
                    <span className="text-dim">Sort by:</span>
                    {" "}
                    {(value ? SORT_BY_TO_NAME.find(sort => sort.sortBy === value)?.label : undefined) ?? "None"}
                </div>
            </Dropdown.Toggle>
            <Dropdown.Menu className={`p3-2 gap-1 ${Styles["panel-filters"]}`}>
                {Object.keys(SORT_BY_GROUPS).map(type => (
                    <SortItem key={type} type={type as SortType} value={value} onChange={onChange} />
                ))}
            </Dropdown.Menu>
        </Dropdown>
    );
}

SortByFilter.defaultProps = {
    shouldShowExpand: false,
};
